import type { ProFormColumnsType } from '@ant-design/pro-components';
import type { ExistingFirmResponse } from 'api/firm/firm.types';
import { FormattedMessage } from 'react-intl';
import { submissionQuestions } from 'pages/ApplicationFormPage/Submission/questions';
import { SubmissionFunnelTypeEnum } from 'pages/ApplicationFormPage/Submission/types';
import type { TypedFormatMessageFn } from 'types/intl';
import { getBtlOrganisationForFunnelType, getBtlSubmissionRouteUIConfig } from './btlSubmissionRouteConfig';

interface SubmissionRouteFormSchemaProps {
  formatMessage: TypedFormatMessageFn;
  firm?: ExistingFirmResponse | null;
}

function funnelTypeOptionLabel(formatMessage: TypedFormatMessageFn, value: SubmissionFunnelTypeEnum): string {
  switch (value) {
    case SubmissionFunnelTypeEnum.DIRECT:
      return formatMessage({ id: 'applicationForm.SUBMISSION.submissionFunnelType.option.direct.label' });
    case SubmissionFunnelTypeEnum.MORTGAGE_CLUB:
      return formatMessage({ id: 'applicationForm.SUBMISSION.submissionFunnelType.option.mortgageClub.label' });
    case SubmissionFunnelTypeEnum.NETWORK:
      return formatMessage({ id: 'applicationForm.SUBMISSION.submissionFunnelType.option.network.label' });
  }
}

function OrganisationDetailsPreview({ organisation }: { organisation: { name: string; fcaNumber?: string } }) {
  return (
    <div className='mb-4 rounded border border-gray-200 bg-gray-50 p-4 text-sm'>
      <p className='font-medium text-gray-900'>{organisation.name}</p>
      {organisation.fcaNumber && <p className='mt-1 text-gray-700'>{organisation.fcaNumber}</p>}
    </div>
  );
}

export function submissionRouteFormSchema(props: SubmissionRouteFormSchemaProps): ProFormColumnsType[] {
  if (!props.firm) {
    return [
      {
        renderFormItem: () => <FormattedMessage id='applicationForm.SUBMISSION.firmNotFound' tagName='p' />,
      },
    ];
  }

  const routeConfig = getBtlSubmissionRouteUIConfig(props.firm);
  const isSelectable = routeConfig.selectableOptions.length > 1;
  const visibleOptions = submissionQuestions.submissionFunnelType.options.filter(option =>
    (isSelectable ? routeConfig.selectableOptions : routeConfig.visibleOptions).includes(option.value),
  );

  return [
    {
      title: props.formatMessage({ id: 'applicationForm.SUBMISSION.submissionFunnelType.label' }),
      dataIndex: 'submissionFunnelType',
      valueType: 'radioButton',
      initialValue: routeConfig.defaultOption,
      fieldProps: {
        options: visibleOptions.map(option => ({
          label: funnelTypeOptionLabel(props.formatMessage, option.value),
          value: option.value,
        })),
        buttonStyle: 'solid',
        ...(isSelectable ? {} : { className: 'pointer-events-none' }),
      },
      formItemProps: {
        rules: isSelectable
          ? [{ message: props.formatMessage({ id: 'common.error.requiredField' }), required: true }]
          : undefined,
      },
    },
    {
      valueType: 'dependency',
      name: ['submissionFunnelType'],
      columns: ({ submissionFunnelType }) => {
        if (!submissionFunnelType || submissionFunnelType === SubmissionFunnelTypeEnum.DIRECT) {
          return [];
        }

        const organisation = getBtlOrganisationForFunnelType(props.firm!, submissionFunnelType);

        if (!organisation) {
          return [];
        }

        return [
          {
            renderFormItem: () => <OrganisationDetailsPreview organisation={organisation} />,
          },
        ];
      },
    },
  ];
}
